/**
 * What a broadcast pays. This is the only implementation of the formula.
 *
 * The evaluation tool deliberately cannot compute it: a second copy would drift
 * from this one, and with money nobody would notice. The tool publishes the
 * measured criteria and the rate card; the arithmetic happens here.
 *
 * The shape is fixed by the contract; every rate, cut and criterion in it
 * comes from the season document, so a new season is a new rate card rather
 * than a change here. Nothing below names a criterion that the season itself
 * could have named instead — see `sponsorCriteria` and `defined`.
 *
 * Per game:
 *
 *   reward = gate × [ production × (1 − 0.10·missing sponsor messages
 *                                      − opener cut − lineups cut
 *                                      − technical cut)
 *                   + extra video × count
 *                   + commentary   (zero when the game also ran on ČT Sport+)
 *                   + replay
 *                   + operated camera (0 / half / full, declared) ]
 *
 * `gate` is the product of the four absolute conditions — two cameras, ambient
 * sound, score overlay, the whole game in the recording. Zero in any of them
 * pays nothing at all. It is a product rather than an all-or-nothing flag so
 * that a partial ruling (an admin writing 0.9 for a game whose recording is
 * whole bar one inning) scales the payment instead of wiping it — last
 * season's sheet did exactly that.
 *
 * The script cuts come off the production fee: the opener when it never ran,
 * and the lineups pair, which is why that cut cannot be folded into a
 * per-criterion penalty — missing both costs more than twice missing one.
 * Every rate lives in `season.penalties`, published from the tool's
 * `config.toml`; nothing here is hard-coded.
 *
 * Technical parameters are a cut too, and the largest, scaled by how far
 * short the broadcast fell. They used to be a gate, which meant a 720p stream
 * paid nothing at all — not for the commentary, not for the replay, neither of
 * which is worth less in 720p. Under the 2026-27 rates the share is 40 %: what
 * is left of the fee once the five sponsor messages (10 % each) and the script
 * (10 % at most) are accounted for, so it stands for everything else the
 * production is expected to get right.
 */
import {
  CriterionDef,
  EvaluationDecision,
  EvaluationGame,
  EvaluationPenalties,
  EvaluationSeason,
  Reward,
  RewardLine,
} from '../model/evaluation/evaluation';

/**
 * The lineups pair, which is the one place an id still has to be written down:
 * its arithmetic is about a *pair* — one missing costs a fifth of what both
 * missing costs — and no amount of grouping in the rate card expresses that.
 * Everything else in this file is derived from what the season publishes.
 *
 * A season that does not define them cuts nothing for them; see `defined`.
 */
export const LINEUP_CRITERIA = ['lineupsT1', 'lineupsM1'];
export const OPENER_CRITERION = 'opener';
export const TECHNICAL_CRITERION = 'technical';
export const GATE_ROLE = 'gate';

/**
 * The criteria a season actually has.
 *
 * Every cut below asks this first, and the reason is a money bug waiting for
 * the next contract: `effectiveValue` returns null for a criterion nobody
 * measured, the cuts read null as zero, and zero means *not met*. So a season
 * that simply stopped having an opener would take five percent off every
 * broadcast in it, for a criterion that does not exist. Absent is not unmet.
 */
function defined(season: EvaluationSeason): Set<string> {
  return new Set((season?.criteria ?? []).map(criterion => criterion.id));
}

/**
 * The mandatory sponsor messages, read off the rate card rather than listed
 * here. The contract numbers them by inning and the count is exactly the kind
 * of thing that changes between seasons — six of them with a hard-coded five
 * would pay ten percent too much on every broadcast that missed the new one,
 * and nothing would look wrong.
 */
export function sponsorCriteria(season: EvaluationSeason): string[] {
  return (season?.criteria ?? [])
    .filter(criterion => criterion.group === 'sponsor' && criterion.role === 'penalty')
    .map(criterion => criterion.id);
}

/**
 * Criteria an admin may rule part-way, with the share of the production fee a
 * full miss costs. Everything else is a fact with two answers — a sponsor
 * video either ran or it did not — so a fraction there would be a guess
 * dressed as a measurement.
 *
 * Picture quality is the exception. Resolution, the ghosting the tool cannot
 * see, a feed that wobbled for two innings: these are matters of degree, and
 * last season's sheet already ruled them that way, writing 0.9 where a stream
 * was nearly but not quite right. This is that column, with a rate behind it.
 */
export const SCALABLE_CUTS: { [criterionId: string]: keyof EvaluationPenalties } = {
  [TECHNICAL_CRITERION]: 'technical',
};

/**
 * The most this criterion can take off the production fee, or null when it is
 * not one an admin dials. A season document published before the rate existed
 * returns 0, which reads as "no cut" — never as a cut of unknown size.
 */
export function maximumCut(criterionId: string, season: EvaluationSeason): number | null {
  const key = SCALABLE_CUTS[criterionId];
  return key ? (season?.penalties?.[key] ?? 0) : null;
}

/**
 * The value that counts for a criterion: an admin's ruling wins over the
 * measurement, and the declared camera comes from the producer.
 */
export function effectiveValue(criterionId: string,
                               game: EvaluationGame,
                               decisions: { [criterion: string]: EvaluationDecision } = {}): number | null {
  const decided = decisions[criterionId];
  if (decided && decided.value !== null && decided.value !== undefined) {
    return decided.value;
  }
  if (criterionId === 'operatedCamera') {
    // Never frozen into the release snapshot: the producer declares it after
    // the game reaches them, which is the whole point of the declaration.
    const declared = game.declarations?.operatedCamera;
    return declared === null || declared === undefined ? null : declared;
  }
  const released = game.values?.[criterionId];
  if (released !== null && released !== undefined) {
    return released;
  }
  const measured = game.measurements?.[criterionId];
  return measured && measured.value !== null && measured.value !== undefined ? measured.value : null;
}

/**
 * Criteria still without a value, i.e. what stands between the game and the
 * producer. An `info` criterion never blocks anything, and neither does the
 * declared one — the producer fills that in after the game reaches them.
 */
export function pendingCriteria(game: EvaluationGame,
                                season: EvaluationSeason,
                                decisions: { [criterion: string]: EvaluationDecision } = {}): string[] {
  return (season?.criteria ?? [])
    .filter(criterion => criterion.role !== 'info' && criterion.role !== 'declared')
    .filter(criterion => effectiveValue(criterion.id, game, decisions) === null)
    .map(criterion => criterion.id);
}

/**
 * A game can go to the producer once every criterion has a value — or as soon
 * as an absolute condition is known to have failed, because then nothing else
 * can change the payment.
 *
 * That second case is not a shortcut for its own sake. A broadcast whose
 * recording stops in the fourth inning pays nothing whatever else it did, and
 * ruling on its dozen open items would be decisions that decide nothing.
 *
 * Resolution used to land here as well — Technika Brno broadcasts in 720p and
 * the tool would not even analyse those. It does now, and they pay a cut
 * rather than nothing, so their criteria are worth ruling on again.
 */
export function readyToRelease(game: EvaluationGame,
                               season: EvaluationSeason,
                               decisions: { [criterion: string]: EvaluationDecision } = {}): boolean {
  return pendingCriteria(game, season, decisions).length === 0
    || failedGates(game, season, decisions).length > 0;
}

/**
 * Absolute conditions that are *known* to have failed. An undecided one is not
 * among them: "we have not looked yet" must never read as "not met", or the
 * game would go out with a zero nobody stands behind.
 */
export function failedGates(game: EvaluationGame,
                     season: EvaluationSeason,
                     decisions: { [criterion: string]: EvaluationDecision } = {}): string[] {
  return gateCriteria(season)
    .filter(criterion => {
      const value = effectiveValue(criterion.id, game, decisions);
      return value !== null && value <= 0;
    })
    .map(criterion => criterion.id);
}

function gateCriteria(season: EvaluationSeason): CriterionDef[] {
  return (season?.criteria ?? []).filter(criterion => criterion.role === GATE_ROLE);
}

function labelOf(season: EvaluationSeason, id: string): string {
  return (season?.criteria ?? []).find(criterion => criterion.id === id)?.label ?? id;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * The cut taken off the production fee: ten percent per sponsor message that
 * never ran, plus the script cuts for the opener and the lineups. It can add
 * up past 100 %, so the factor is clamped at zero — the other lines are
 * unaffected either way.
 */
export function productionFactor(game: EvaluationGame,
                                 season: EvaluationSeason,
                                 decisions: { [criterion: string]: EvaluationDecision } = {}): {
  factor: number; reasons: string[];
} {
  const penalties = season.penalties;
  const have = defined(season);
  const reasons: string[] = [];

  // An undecided message counts against the fee, so the figure an admin sees
  // is never rosier than the truth — but the wording says how many of them are
  // still open, because "−50% for 5 messages" on a game where two were merely
  // unmeasured reads as an accusation.
  let missingSponsors = 0;
  let undecidedSponsors = 0;
  sponsorCriteria(season).forEach(id => {
    const value = effectiveValue(id, game, decisions);
    if (value === null) {
      undecidedSponsors += 1;
    }
    missingSponsors += 1 - clamp01(value ?? 0);
  });
  const sponsorCut = missingSponsors * penalties.sponsorMessage;
  if (missingSponsors > 0) {
    const rounded = Math.round(missingSponsors * 100) / 100;
    reasons.push(`−${Math.round(sponsorCut * 100)}% for ${rounded} sponsor message${rounded === 1 ? '' : 's'}`
      + (undecidedSponsors ? ` (${undecidedSponsors} still undecided)` : ''));
  }

  // The opener is measured like any other criterion, but it pays nothing on
  // its own — it only takes five percent off the production fee when missing.
  const opener = clamp01(effectiveValue(OPENER_CRITERION, game, decisions) ?? 0);
  const openerCut = have.has(OPENER_CRITERION)
    ? (1 - opener) * (penalties.opener ?? 0)
    : 0;
  if (openerCut > 0) {
    reasons.push(`−${Math.round(openerCut * 100)}% for the opener`);
  }

  const lineups = LINEUP_CRITERIA.filter(id => have.has(id));
  const shown = lineups
    .map(id => clamp01(effectiveValue(id, game, decisions) ?? 0))
    .reduce((sum, value) => sum + value, 0);
  const missingLineups = lineups.length - shown;
  let lineupCut = 0;
  if (missingLineups > 1) {
    lineupCut = penalties.lineupsMissing;
    reasons.push(`−${Math.round(lineupCut * 100)}% for no lineups`);
  } else if (missingLineups > 0) {
    lineupCut = penalties.lineupsPartial;
    reasons.push(`−${Math.round(lineupCut * 100)}% for one lineup only`);
  }

  // Technical parameters, scaled: a full miss is the whole 40 %, a ruling of
  // 0.9 is four percent. Unlike the others this is a measured number rather
  // than a yes/no, so the wording carries what was actually measured — "−40%"
  // with no explanation reads as a penalty nobody can check.
  const technicalMax = have.has(TECHNICAL_CRITERION)
    ? (maximumCut(TECHNICAL_CRITERION, season) ?? 0)
    : 0;
  const technicalValue = effectiveValue(TECHNICAL_CRITERION, game, decisions);
  const technicalCut = (1 - clamp01(technicalValue ?? 0)) * technicalMax;
  if (technicalCut > 0) {
    const measured = game.measurements?.[TECHNICAL_CRITERION]?.note;
    reasons.push(`−${Math.round(technicalCut * 100)}% for the technical parameters`
      + (technicalValue === null ? ' (still undecided)' : measured ? ` (${measured})` : ''));
  }

  return {
    factor: Math.max(0, 1 - sponsorCut - openerCut - lineupCut - technicalCut),
    reasons,
  };
}

/**
 * One line of the payment. `detail` is left off entirely when there is nothing
 * to say, rather than set to undefined: the approved reward is written to
 * Firestore as it stands here, and Firestore rejects an undefined field value.
 */
function line(id: string, label: string, amount: number, detail?: string): RewardLine {
  return detail ? {id, label, amount, detail} : {id, label, amount};
}

/** The whole payment, line by line, ready to show to the producer. */
export function computeReward(game: EvaluationGame,
                              season: EvaluationSeason,
                              decisions: { [criterion: string]: EvaluationDecision } = {}): Reward {
  const rates = season.rates;
  const pending = pendingCriteria(game, season, decisions);

  const blockedBy = failedGates(game, season, decisions);
  const gate = gateCriteria(season)
    .reduce((product, criterion) => product * clamp01(effectiveValue(criterion.id, game, decisions) ?? 0), 1);

  const {factor, reasons} = productionFactor(game, season, decisions);
  // A line is only drawn for something this season actually pays for. The
  // labels come from the rate card too, so a renamed criterion renames its
  // line instead of leaving a caption from a contract that has expired.
  const have = defined(season);
  const named = (id: string, fallback: string) =>
    (season?.criteria ?? []).find(criterion => criterion.id === id)?.label ?? fallback;
  const lines: RewardLine[] = [];

  lines.push(line('production', 'Production, cut and direction',
    Math.round(rates.production * factor * gate),
    reasons.length ? reasons.join(', ') : undefined));

  const extraVideos = effectiveValue('extraVideos', game, decisions) ?? 0;
  if (have.has('extraVideos') && extraVideos > 0) {
    lines.push(line('extraVideos', named('extraVideos', 'Promo videos beyond the mandatory set'),
      Math.round(rates.extraVideo * extraVideos * gate),
      `${extraVideos} × ${rates.extraVideo}`));
  }

  if (have.has('liveCommentary')) {
    const commentary = clamp01(effectiveValue('liveCommentary', game, decisions) ?? 0);
    lines.push(line('liveCommentary', named('liveCommentary', 'Own live commentary'),
      game.ctSportPlus ? 0 : Math.round(rates.commentary * commentary * gate),
      game.ctSportPlus
        ? 'Also on ČT Sport+ — the commentary is paid by the association and ČT'
        : undefined));
  }

  if (have.has('replay')) {
    const replay = clamp01(effectiveValue('replay', game, decisions) ?? 0);
    lines.push(line('replay', named('replay', 'Replay'),
      Math.round(rates.replay * replay * gate)));
  }

  if (have.has('operatedCamera')) {
    const camera = effectiveValue('operatedCamera', game, decisions);
    lines.push(line('operatedCamera', named('operatedCamera', 'Operated camera on live play'),
      Math.round(rates.operatedCamera * clamp01(camera ?? 0) * gate),
      camera === 0.5 ? 'Operated camera, but not on live play — half rate' : undefined));
  }

  const total = lines.reduce((sum, line) => sum + line.amount, 0);

  return {
    total,
    lines,
    blocked: blockedBy.length > 0,
    blockedBy,
    pending,
    currency: season.currency,
    rates,
  };
}

/** Human wording for why nothing is paid, using the season's own labels. */
export function blockedReason(reward: Reward, season: EvaluationSeason): string {
  if (!reward.blocked) {
    return '';
  }
  const names = reward.blockedBy.map(id => labelOf(season, id));
  return `No payment: ${names.join(', ')} not met`;
}
